import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';

const PDFContext = createContext();

export const usePDF = () => {
  const context = useContext(PDFContext);
  if (!context) throw new Error('usePDF must be used within a PDFProvider');
  return context;
};

export const PDFProvider = ({ children }) => {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentTool, setCurrentTool] = useState(null);
  const [zoom, setZoom] = useState(1.25);
  const [selectedPages, setSelectedPages] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') !== 'light');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  // All element positions are stored in PDF points (native PDF coordinates)
  const [textElements, setTextElements] = useState([]);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [pendingText, setPendingText] = useState(null);

  const [imageElements, setImageElements] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);

  const [highlightConfig, setHighlightConfig] = useState({ color: '#ffff00', opacity: 0.3 });

  // ==================
  // EDITOR LOCK (pay-before-edit)
  // ==================
  const [lockStatus, setLockStatus] = useState({ loading: true, signedIn: false, allowed: false, isPro: false, credits: 0, remaining: 0 });

  const refreshLock = useCallback(async () => {
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    try {
      const meRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (!meRes.ok) {
        setLockStatus({ loading: false, signedIn: false, allowed: false, isPro: false, credits: 0, remaining: 0 });
        return;
      }
      const me = await meRes.json();
      const isPro = me?.is_pro || me?.plan === 'lifetime' || me?.plan === 'monthly' || me?.is_staff;
      const res = await fetch(`${API}/downloads/check`, { credentials: 'include' });
      const d = await res.json();
      setLockStatus({
        loading: false,
        signedIn: true,
        allowed: !!d.allowed,
        isPro,
        credits: d.credits || 0,
        remaining: d.remaining || 0,
      });
    } catch {
      setLockStatus(s => ({ ...s, loading: false }));
    }
  }, []);

  // Evaluate the edit guard on mount and the moment the auth session changes (e.g., right after sign-in)
  const { user: authUser } = useAuth();
  useEffect(() => {
    refreshLock();
  }, [authUser?.user_id, refreshLock]);

  // Locked when: not signed in, OR (not pro AND no credits AND free daily download already used)
  const isLocked = !lockStatus.loading && (!lockStatus.signedIn || !lockStatus.allowed);

  // ==================
  // UNDO / REDO SYSTEM
  // ==================
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const getSnapshot = useCallback(() => ({
    textElements: JSON.parse(JSON.stringify(textElements)),
    imageElements: JSON.parse(JSON.stringify(imageElements)),
  }), [textElements, imageElements]);

  const clearUndoRedo = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  const pushUndo = useCallback(() => {
    const snap = {
      textElements: JSON.parse(JSON.stringify(textElements)),
      imageElements: JSON.parse(JSON.stringify(imageElements)),
    };
    undoStackRef.current.push(snap);
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, [textElements, imageElements]);

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const currentSnap = {
      textElements: JSON.parse(JSON.stringify(textElements)),
      imageElements: JSON.parse(JSON.stringify(imageElements)),
    };
    redoStackRef.current.push(currentSnap);
    const prev = undoStackRef.current.pop();
    setTextElements(prev.textElements);
    setImageElements(prev.imageElements);
    setSelectedTextId(null);
    setSelectedImageId(null);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
  }, [textElements, imageElements]);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const currentSnap = {
      textElements: JSON.parse(JSON.stringify(textElements)),
      imageElements: JSON.parse(JSON.stringify(imageElements)),
    };
    undoStackRef.current.push(currentSnap);
    const next = redoStackRef.current.pop();
    setTextElements(next.textElements);
    setImageElements(next.imageElements);
    setSelectedTextId(null);
    setSelectedImageId(null);
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
  }, [textElements, imageElements]);

  // Delete selected object
  const deleteSelected = useCallback(() => {
    if (selectedTextId) {
      pushUndo();
      setTextElements(prev => prev.filter(t => t.id !== selectedTextId));
      setSelectedTextId(null);
      return true;
    }
    if (selectedImageId) {
      pushUndo();
      setImageElements(prev => prev.filter(i => i.id !== selectedImageId));
      setSelectedImageId(null);
      return true;
    }
    return false;
  }, [selectedTextId, selectedImageId, pushUndo]);

  // Global keyboard handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Delete / Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        if (deleteSelected()) e.preventDefault();
      }
      // Ctrl+Z / Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl+Y / Cmd+Shift+Z
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedTextId(null);
        setSelectedImageId(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteSelected, undo, redo]);

  const resetPDF = useCallback(() => {
    setPdfFile(null);
    setPdfInfo(null);
    setPages([]);
    setCurrentTool(null);
    setZoom(1.25);
    setSelectedPages([]);
    setIsProcessing(false);
    setProcessingMessage('');
    setTextElements([]);
    setImageElements([]);
    setSelectedTextId(null);
    setSelectedImageId(null);
    setPendingText(null);
    setPendingImage(null);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => {
      if (!prev) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', !prev ? 'dark' : 'light');
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    pdfFile, setPdfFile, pdfInfo, setPdfInfo, pages, setPages,
    currentTool, setCurrentTool, zoom, setZoom,
    selectedPages, setSelectedPages, resetPDF,
    isDarkMode, toggleDarkMode,
    isProcessing, setIsProcessing, processingMessage, setProcessingMessage,
    textElements, setTextElements, selectedTextId, setSelectedTextId,
    pendingText, setPendingText,
    imageElements, setImageElements, selectedImageId, setSelectedImageId,
    pendingImage, setPendingImage,
    highlightConfig, setHighlightConfig,
    isLocked, lockStatus, refreshLock,
    pushUndo, undo, redo, canUndo, canRedo, deleteSelected, getSnapshot, clearUndoRedo,
  };

  return <PDFContext.Provider value={value}>{children}</PDFContext.Provider>;
};
